/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ReactNode } from "react";
import { Box, Empty, Table } from "./widgets";
import { IN, crestColor, dash, fmtDate, formatShort, fx, initials, n0, nz, formatLabel } from "./model";
import { useCareer } from "./OverviewPane";

export const TeamsPane = ({ teams, overview }: { teams: any[]; overview: any }) => {
  const comps = nz<any>(overview?.tournamentMiniStats).filter((t) => n0(t.matchesPlayed) > 0);
  return (
    <>
      {teams.length ? (
        <div className="teamcards">
          {teams.map((t, i) => {
            const w = n0(t.winMatchesCount), l = n0(t.lossMatchesCount), d = n0(t.drawMatchesCount);
            return (
              <div className="box tc" key={t.teamSnapshotId ?? t.teamId ?? i}>
                <span className="crest" style={{ background: crestColor(t.teamName) }}>{initials(t.teamName)}</span>
                <div><h4>{t.teamName}</h4>{t.tournamentName && <div className="m">{t.tournamentName}</div>}{t.teamRole && <div className="m">{String(t.teamRole).replace(/_/g, " ").toLowerCase()}</div>}</div>
                <div className="st"><div><b>{w + l + d}</b>Played</div><div><b>{w}</b>Won</div><div><b>{l}</b>Lost</div><div><b>{d}</b>Tie</div></div>
              </div>
            );
          })}
        </div>
      ) : <Box><Empty text="Teams this player is part of will appear here." /></Box>}
      {comps.length > 0 && (
        <Box title="Leagues and tournaments">
          <Table head={["Tournament", "League", "Matches", "Runs", "HS", "Avg", "Wkts"]} left={[1]}
            rows={comps.map((c) => [c.tournament?.tournamentName ?? dash, c.tournament?.leagueName ?? dash, n0(c.matchesPlayed), IN(n0(c.runsScored)), n0(c.highestScore), fx(c.average), n0(c.wicketsTaken)])} />
        </Box>
      )}
    </>
  );
};

const ICON_BAT = (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M15.5 3.5l5 5-9.5 9.5-5-5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M6 13l-3.5 3.5a1.4 1.4 0 000 2l3 3a1.4 1.4 0 002 0L11 18" stroke="currentColor" strokeWidth="1.8" /></svg>);
const ICON_MEDAL = (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="14.5" r="5.5" stroke="currentColor" strokeWidth="1.8" /><path d="M8.5 9.8L6 3h4l2 5 2-5h4l-2.5 6.8" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>);
const ICON_BALL = (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" /><path d="M7.5 5.5c2.5 4 2.5 9 0 13M16.5 5.5c-2.5 4-2.5 9 0 13" stroke="currentColor" strokeWidth="1.6" /></svg>);

export const RecordsPane = ({ overview }: { overview: any }) => {
  const { batAll, bowlAll } = useCareer(overview);
  const topInn = nz<any>(overview?.topBattingInnings);
  const hundreds = topInn.filter((x) => n0(x.runsScored) >= 100);
  const miles = nz<any>(overview?.careerMilestones);
  const potm = nz<any>(overview?.recentMatches).filter((m) => m.isPlayerOfMatch);
  const shelf: [ReactNode, string | number, string, string][] = [[ICON_BAT, batAll.h + batAll.f, "50+ scores", ""], [ICON_BAT, batAll.h, "Hundreds", ""], [ICON_BALL, bowlAll.w3 + bowlAll.w5, "3+ wicket hauls", ""], [ICON_MEDAL, potm.length, "Player of the match (recent)", "g"]];
  return (
    <>
      <div className="shelf">{shelf.map(([i, v, k, c]) => (<div className="box trophy" key={k}><span className={`ic ${c}`}>{i}</span><div><div className="v">{v}</div><div className="k">{k}</div></div></div>))}</div>
      <Box title="Best innings" sub="Highest scores">
        {topInn.length ? (
          <div className="tons">{topInn.map((t, i) => { const mm = t.matchMiniResponse ?? {}; return (
            <div className="ton" key={i}>{i === 0 && <span className="best">Career best</span>}
              <div className="sc">{t.runsScored}{mm.notOut ? "*" : ""}<small>{t.ballsFaced} balls</small></div>
              <div className="vs">vs {mm.opponentTeamName ?? dash}</div><div className="meta">{mm.tournamentName}</div><div className="meta">{fmtDate(mm.matchDate)}</div>
              <span className="fmt">{formatShort(mm.matchType)}</span></div>); })}</div>
        ) : <Empty text="No innings yet." />}
        {hundreds.length === 0 && topInn.length > 0 && <div className="box-f"><span>No hundreds yet.</span></div>}
      </Box>
      <Box title="Milestones" sub="Newest first">
        {miles.length ? (<ul className="tl">{miles.map((m, i) => (<li key={i} className={i === 0 ? "key" : ""}><span className="yr">{fmtDate(m.achievedOn)}</span><b>{m.milestone}</b><span>{m.matchMiniResponse ? `vs ${m.matchMiniResponse.opponentTeamName}, ${m.matchMiniResponse.tournamentName ?? ""} · ${formatLabel(m.matchMiniResponse.matchType)}` : ""}</span></li>))}</ul>) : <Empty text="No milestones yet." />}
      </Box>
    </>
  );
};
